/**
 * Color Palette Generator
 *
 * Generates a full shade palette (25, 50, 100-900) from a single base color.
 * This allows whitelabel clients to set a primary color and have all shades
 * automatically generated for design system consistency.
 *
 * Strategy:
 * - Converts hex to HSL (hue, saturation, lightness)
 * - Preserves hue and saturation from base color
 * - Interpolates lightness values for each shade
 * - Lighter shades (25-400): interpolate between white and base
 * - 500: returns base color as-is (reference point)
 * - Darker shades (600-900): interpolate between base and near-black
 */

/**
 * Convert hex color to HSL
 * @param hex - Hex color string (#RGB, #RRGGBB)
 * @returns HSL object with h (0-360), s (0-100), l (0-100)
 */
export const hexToHsl = (hex: string): { h: number; s: number; l: number } => {
  // Remove # if present
  hex = hex.replace(/^#/, '');

  // Parse hex to RGB
  let r: number, g: number, b: number;
  if (hex.length === 3) {
    r = parseInt(hex[0] + hex[0], 16) / 255;
    g = parseInt(hex[1] + hex[1], 16) / 255;
    b = parseInt(hex[2] + hex[2], 16) / 255;
  } else {
    r = parseInt(hex.substring(0, 2), 16) / 255;
    g = parseInt(hex.substring(2, 4), 16) / 255;
    b = parseInt(hex.substring(4, 6), 16) / 255;
  }

  // Calculate HSL
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

    switch (max) {
      case r:
        h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
        break;
      case g:
        h = ((b - r) / d + 2) / 6;
        break;
      case b:
        h = ((r - g) / d + 4) / 6;
        break;
    }
  }

  return {
    h: Math.round(h * 360),
    s: Math.round(s * 100),
    l: Math.round(l * 100),
  };
};

/**
 * Convert HSL to hex color
 * @param h - Hue (0-360)
 * @param s - Saturation (0-100)
 * @param l - Lightness (0-100)
 * @returns Hex color string (#RRGGBB)
 */
export const hslToHex = (h: number, s: number, l: number): string => {
  // Normalize values
  h = h / 360;
  s = s / 100;
  l = l / 100;

  let r: number, g: number, b: number;

  if (s === 0) {
    r = g = b = l; // achromatic
  } else {
    const hue2rgb = (p: number, q: number, t: number): number => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1 / 6) return p + (q - p) * 6 * t;
      if (t < 1 / 2) return q;
      if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
      return p;
    };

    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;

    r = hue2rgb(p, q, h + 1 / 3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1 / 3);
  }

  const toHex = (x: number): string => {
    const hex = Math.round(x * 255).toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  };

  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};

/**
 * Generate full color palette from a base hex color
 * @param baseHex - Base hex color (#RRGGBB)
 * @returns Object with shade keys (25, 50, 100-900) and hex values
 */
export const generateColorPalette = (baseHex: string): Record<string, string> => {
  // Parse base color to HSL
  const base = hexToHsl(baseHex);

  // Lightness targets for each shade
  // Strategy: interpolate based on base lightness to avoid weird results
  const baseLightness = base.l;

  // Helper: interpolate between two values
  const lerp = (start: number, end: number, t: number): number => {
    return start + (end - start) * t;
  };

  // Generate lightness for each shade
  const getLightness = (shade: string): number => {
    switch (shade) {
      case '25':
        return lerp(100, baseLightness, 0.05); // Very close to white
      case '50':
        return lerp(100, baseLightness, 0.15);
      case '100':
        return lerp(100, baseLightness, 0.3);
      case '200':
        return lerp(100, baseLightness, 0.5);
      case '300':
        return lerp(100, baseLightness, 0.65);
      case '400':
        return lerp(100, baseLightness, 0.82);
      case '500':
        return baseLightness; // Base color
      case '600':
        return lerp(baseLightness, 5, 0.25);
      case '700':
        return lerp(baseLightness, 5, 0.5);
      case '800':
        return lerp(baseLightness, 5, 0.7);
      case '900':
        return lerp(baseLightness, 5, 0.85);
      default:
        return baseLightness;
    }
  };

  // Generate palette
  const shades = ['25', '50', '100', '200', '300', '400', '500', '600', '700', '800', '900'];
  const palette: Record<string, string> = {};

  shades.forEach((shade) => {
    const lightness = getLightness(shade);
    palette[shade] = hslToHex(base.h, base.s, lightness);
  });

  return palette;
};
